/* eslint-disable ember/no-computed-properties-in-native-classes, ember/no-side-effects, ember/no-runloop */
import { computed, set } from '@ember/object';
import { dependentKeyCompat } from '@ember/object/compat';
import { getOwner } from '@ember/owner';
import { begin, end } from '@ember/runloop';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PRISM_SPACING_1000 } from '@mylighthouse/prism-foundation';
import dayjs from 'dayjs';
import { t } from 'ember-intl';
import { and, not } from 'ember-truth-helpers';
import {
  EXTRAPOLATED_DATA,
  OTB_DEFAULT_VALUE,
} from 'frontend-utils/utils/constants/constant-values';
import GeneralChartsOtaChart from 'lumen/components/general/charts/ota-chart';
import GeneralTooltipsTooltipOtbPace from 'lumen/components/general/tooltips/tooltip-otb-pace';
import {
  CAPACITY,
  LAST_YEAR,
  MARKET_OTB,
  SHORT_TERM_RENTAL_OTB_COLOR,
  TRANSIENT,
  TRANSIENT_FILL,
} from 'lumen/utils/constants/chart-color-constants';
import { OutOfOrderRooms } from 'lumen/utils/models/occupancy';
import { getDefaultTooltipOptions } from 'lumen/utils/rates-chart';

interface GeneralDayDetailChartsChartBookingCurveSignature {
  Args: {
    containerHeight: unknown;
    dayMoment: unknown;
    message: unknown;
    rentalData: unknown;
    sortedPeriods: unknown;
  };
}

export default class GeneralDayDetailChartsChartBookingCurve extends Component<GeneralDayDetailChartsChartBookingCurveSignature> {
  @service declare access: Services['access'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];

  endOfExtrapolatedLastYearData = null;

  pmsIntegrationDateInCurrentRange = null;

  tooltip = getDefaultTooltipOptions();

  @computed(
    'access.{hasShortTermRentalAccess,hasShortTermRentalOnlyStrTab}',
    'args.sortedPeriods.[]',
    'endOfExtrapolatedLastYearData',
    'hasHistoricPace',
    'rentalOccupanciesMap',
  )
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartData() {
    const { sortedPeriods } = this.args;

    // @ts-expect-error: Incorrect type
    if (!sortedPeriods || sortedPeriods.length === 0) {
      return [];
    }

    const data = [];
    // @ts-expect-error: Incorrect type
    const single = [];
    // @ts-expect-error: Incorrect type
    const inventoryExcludingOOORooms = [];
    // @ts-expect-error: Incorrect type
    const shortTermRental = [];
    // @ts-expect-error: Incorrect type
    const sum = [];
    // @ts-expect-error: Incorrect type
    const group = [];
    // @ts-expect-error: Incorrect type
    const sumLastYear = [];
    // @ts-expect-error: Incorrect type
    const marketOtb = [];

    const hasTransientGroupSplit = this.access.hasFeature(
      'pms.transient_group_split',
    );
    const hasMarketOtb = this.access.hasFeature('otb.market_benchmark');

    // Start at zero, tooltips will not display this
    // @ts-expect-error: Incorrect type
    let previousRoomsBookedGroup;
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    let previousLastYearMessage = (this.args.sortedPeriods ?? [])[0]?.lastyear
      ?.occupancy?.message;

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.args.sortedPeriods.forEach((item) => {
      const itemExtractDateUnix = this.hotelAndMarketService.toLocalUnixTime(
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        item.extractDate.unix(),
      );

      // Don't show any extrapolated Group/Transient data for this year
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      const isExtraPolated = item.occupancy?.message === EXTRAPOLATED_DATA;

      // While looping the data points, keep track of when the data stops to be extrapolated for last year data
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      const currentLastYearMessage = item.lastYear?.occupancy?.message;
      if (
        previousLastYearMessage === EXTRAPOLATED_DATA &&
        currentLastYearMessage !== EXTRAPOLATED_DATA
      ) {
        set(this, 'endOfExtrapolatedLastYearData', itemExtractDateUnix);
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      previousLastYearMessage = currentLastYearMessage;

      if (hasTransientGroupSplit) {
        // Collect all group bookings
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (item.occupancy?.roomsBookedGroup !== OTB_DEFAULT_VALUE) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          previousRoomsBookedGroup = isExtraPolated
            ? null
            : // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              item.occupancy?.occupancyGroupPercentage;
          group.push({
            x: itemExtractDateUnix,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            y: previousRoomsBookedGroup,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            lastUpdate: item.extractDate,
            marker: { enabled: false },
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            occupancy: item.occupancy,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            occupancyLastYear: item.lastYear?.occupancy,
          });
        } else if (
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          item.occupancy?.totalCapacity !== OTB_DEFAULT_VALUE &&
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          item.occupancy?.roomsBooked !== OTB_DEFAULT_VALUE
        ) {
          // Use the last valid point when group data is missing but totalCapacity is not.
          // This is only done for group data because it's normal group data is "jumpy" while transient data should be more fluent so there we let HighCharts interconnect missing points.
          group.push({
            x: itemExtractDateUnix,
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            y: previousRoomsBookedGroup,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            lastUpdate: item.extractDate,
            marker: { enabled: false },
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            occupancy: item.occupancy,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            occupancyLastYear: item.lastYear?.occupancy,
          });
        }

        // Collect all individual bookings
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (item.occupancy?.roomsBookedIndividual !== OTB_DEFAULT_VALUE) {
          single.push({
            x: itemExtractDateUnix,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            y: isExtraPolated
              ? 0
              : // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                item.occupancy?.occupancyIndividualPercentage,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            lastUpdate: item.extractDate,
            marker: { enabled: false },
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            occupancy: item.occupancy,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            occupancyLastYear: item.lastYear?.occupancy,
          });
        }
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      } else if (item.occupancy?.roomsBooked !== OTB_DEFAULT_VALUE) {
        // In case there's no transient/group split: return the sum
        sum.push({
          x: itemExtractDateUnix,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          y: isExtraPolated ? null : item.occupancy?.occupancyPercentage,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          lastUpdate: item.extractDate,
          marker: { enabled: false },
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          occupancy: item.occupancy,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          occupancyLastYear: item.lastYear?.occupancy,
        });
      }

      if (
        this.access.hasShortTermRentalAccess &&
        !this.access.hasShortTermRentalOnlyStrTab
      ) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const rentalOccupancy =
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          this.rentalOccupanciesMap[item.formattedExtractDate];

        // Don't add missing OTB data or missing marker
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (!rentalOccupancy.isMissing) {
          shortTermRental.push({
            x: itemExtractDateUnix,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            y: rentalOccupancy.value,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            lastUpdate: rentalOccupancy.extractDate,
            marker: { enabled: false },
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            occupancy: item.occupancy,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            occupancyLastYear: item.lastYear?.occupancy,
          });
        }
      }

      // Capacity is 100%, so add 1 instead of the totalCapacity because it's in percentage.
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      let capacityOrInventoryExclOOO = item.occupancy?.totalCapacity
        ? 1
        : // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          item.occupancy?.totalCapacity;

      // @ts-expect-error: Incorrect type
      const outOfNewRooms = new OutOfOrderRooms(getOwner(this));

      if (outOfNewRooms.isSupported) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        capacityOrInventoryExclOOO =
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          item.occupancy?.inventoryExcludingOOORoomsPercentage;
      }

      if (capacityOrInventoryExclOOO !== OTB_DEFAULT_VALUE) {
        inventoryExcludingOOORooms.push({
          x: itemExtractDateUnix,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          y: capacityOrInventoryExclOOO,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          lastUpdate: item.extractDate,
          marker: { enabled: false },
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          occupancy: item.occupancy,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          occupancyLastYear: item.lastYear?.occupancy,
        });
      }

      if (hasMarketOtb) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        const y = item.occupancy?.benchmarkValueNormalized;

        marketOtb.push({
          x: itemExtractDateUnix,
          // Round the Market OTB chart values, because lines weren't straight (decimals in chart data vs. rounded values in tooltip).
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          y: y ? Math.round(y * 100) / 100 : y,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          lastUpdate: item.extractDate,
          marker: { enabled: false },
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          occupancy: item.occupancy,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          occupancyLastYear: item.lastYear?.occupancy,
        });
      }

      // Last year's sum for everybody as of new feature OTA-1998
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (item.lastYear?.occupancy?.roomsBooked !== OTB_DEFAULT_VALUE) {
        sumLastYear.push({
          x: itemExtractDateUnix,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          y: item.lastYear?.occupancy?.occupancyPercentage,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          lastUpdate: item.extractDate,
          marker: { enabled: false },
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          occupancy: item.occupancy,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          occupancyLastYear: item.lastYear?.occupancy,
        });
      }
    });

    if (hasTransientGroupSplit) {
      data.push({
        // @ts-expect-error: Incorrect type
        data: single,
        name: this.intl.t('rates.booking_curve.individual').toString(),
        fillColor: TRANSIENT_FILL,
        color: TRANSIENT,
        lineWidth: 3,
      });

      data.push({
        // @ts-expect-error: Incorrect type
        data: group,
        name: this.intl.t('rates.booking_curve.groups').toString(),
        fillColor: 'var(--prism-color-chart-info-figure-normal)',
        color: 'var(--prism-color-chart-info-figure-normal)',
        lineWidth: 3,
        zIndex: 10,
      });
    } else {
      // In case there's no transient/group split: return the sum
      data.push({
        // @ts-expect-error: Incorrect type
        data: sum,
        name: this.intl.t('general.this_year').toString(),
        color: 'var(--prism-color-chart-info-figure-normal)',
        lineColor: 'var(--prism-color-chart-info-figure-normal)',
        lineWidth: 3,
      });
    }

    const lastYearData = {
      visible: !hasMarketOtb, // Hide last year serie by default when market OTB is available (can still be toggled on)
      // @ts-expect-error: Incorrect type
      data: sumLastYear,
      name: this.intl.t('general.last_year').toString(),
      type: 'line',
      zIndex: 20,
      color: LAST_YEAR,
      lineWidth: 2,
      marker: {
        enabled: false,
      },
    };

    // Add dotted LY line indicating incomplete data (for subscriptions without historic pace)
    if (!this.hasHistoricPace && this.endOfExtrapolatedLastYearData) {
      // @ts-expect-error: Incorrect type
      lastYearData.zoneAxis = 'x';
      // @ts-expect-error: Incorrect type
      lastYearData.zones = [
        {
          value: this.endOfExtrapolatedLastYearData,
          dashStyle: 'dash',
        },
        {
          dashStyle: 'solid',
        },
      ];
    }
    data.push(lastYearData);

    if (hasMarketOtb) {
      data.push({
        // @ts-expect-error: Incorrect type
        data: marketOtb,
        name: this.intl.t('rates.tooltip.market_otb').toString(),
        type: 'line',
        color: MARKET_OTB,
        zIndex: 30,
        marker: {
          enabled: false,
        },
      });
    }

    // @ts-expect-error: Incorrect type
    const outOfNewRooms = new OutOfOrderRooms(getOwner(this));

    data.push({
      // @ts-expect-error: Incorrect type
      data: inventoryExcludingOOORooms,
      name: outOfNewRooms.inventoryText,
      type: 'line',
      color: CAPACITY,
      marker: {
        enabled: false,
      },
    });

    if (
      this.access.hasShortTermRentalAccess &&
      !this.access.hasShortTermRentalOnlyStrTab
    ) {
      data.push({
        // @ts-expect-error: Incorrect type
        data: shortTermRental,
        name: this.intl.t('short_term_rental.otb').toString(),
        type: 'line',
        zIndex: 20, // Added this z-index because the line can get behind the OTB data area chart
        color: SHORT_TERM_RENTAL_OTB_COLOR,
      });
    }

    return data;
  }

  @computed(
    'correctedContainerHeight',
    'maxLeadtime',
    'minLeadtime',
    'pmsIntegrationDate',
    'points',
    'value',
  )
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartOptions() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const { maxLeadtime } = this;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const { minLeadtime } = this;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const max = minLeadtime
      ? // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        this.hotelAndMarketService.toLocalUnixTime(minLeadtime.unix())
      : minLeadtime;
    const nrOfDays = 365;

    // The General::Tooltips::TooltipBookingCurve content depends on whether the PMS integration date is still visible or not
    const pmsIntegrationDateInCurrentRange = dayjs(
      this.pmsIntegrationDate,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-argument
    ).isBetween(maxLeadtime, minLeadtime);
    set(
      this,
      'pmsIntegrationDateInCurrentRange',
      pmsIntegrationDateInCurrentRange,
    );

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;
    // If we are looking at dates in the past,
    // we use the period at leadtime 0 to compare, otherwise we use today
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const dateToCompare =
      !minLeadtime ||
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      this.hotelAndMarketService.getLocalTime().isBefore(minLeadtime)
        ? this.hotelAndMarketService.getLocalTime()
        : minLeadtime;

    let min;
    // Show 90 days before predictions, or for days in the past we simply show 90 days in total
    if (
      maxLeadtime &&
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      dateToCompare.subtract(nrOfDays, 'day').isBefore(maxLeadtime)
    ) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      min = this.hotelAndMarketService.toLocalUnixTime(maxLeadtime.unix());
    } else {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      min = maxLeadtime
        ? this.hotelAndMarketService.toLocalUnixTime(
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
            dateToCompare.subtract(nrOfDays, 'day').startOf('day').unix(),
          )
        : maxLeadtime;
    }

    return {
      chart: {
        type: 'area',
        marginTop: PRISM_SPACING_1000,
        height: this.correctedContainerHeight,
      },
      title: {
        text: 'booking-curve-chart',
      },
      xAxis: {
        allowDecimals: false,
        type: 'datetime',
        ordinal: false,
        title: {
          text: this.intl.t('rates.booking_curve.leadtime').toString(),
        },
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        min,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        max,
      },
      yAxis: {
        opposite: true,
        showFirstLabel: false,
        offset: 25,
        max: 1,
        min: 0,
        title: {
          text: this.intl.t('revenue_insight.occupancy'),
        },
        plotLines: [
          {
            value: 0,
            width: 1,
          },
        ],
        labels: {
          // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
          formatter() {
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            return that.intl.formatNumber(this.value, { style: 'percent' });
          },
        },
      },
      tooltip: {
        shared: true,
        crosshairs: true,
        padding: 0,
        shadow: false,
        borderWidth: 0,
        style: {
          'max-width': 0,
        },
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        formatter() {
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          const extractDate = this.points[0].point.lastUpdate;
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
          const items = this.points || that.splat(this);
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          const firstItemPoint = items[0].point;

          begin();
          set(that, 'firstItemPoint', firstItemPoint);
          set(that, 'extractDate', extractDate);
          if (
            that.access.hasShortTermRentalAccess &&
            !that.access.hasShortTermRentalOnlyStrTab
          ) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            const rentalOccupancy =
              // @ts-expect-error: Incorrect type
              that.rentalOccupanciesMap[
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                dayjs(extractDate).format('YYYY-MM-DD')
              ];
            set(that, 'rentalOccupancy', rentalOccupancy);
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            set(that, 'rentalReferenceOccupancy', rentalOccupancy.lastYear);
          }
          end();

          // @ts-expect-error: Incorrect type
          return document.getElementById('dummy-div-for-tooltip-booking-curve')
            .innerHTML;
        },
        useHTML: true,
      },
      plotOptions: {
        area: {
          stacking: 'normal',
          connectNulls: true,
        },
      },
      legend: {
        enabled: true,
        borderWidth: 0,
        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'bottom',
        itemStyle: {
          width: 150,
          color: 'var(--prism-color-text-neutral-subdued)',
        },
        itemHoverStyle: {
          color: 'var(--prism-color-interactive-text-secondary-hover)',
        },
      },
    };
  }

  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get correctedContainerHeight() {
    return this.args.containerHeight;
  }

  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasHistoricPace() {
    return this.hotelAndMarketService.hotel?.pmsHasHistoricPace;
  }

  @computed('args.sortedPeriods.[]')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get maxLeadtime() {
    const { sortedPeriods } = this.args;

    if (!sortedPeriods) {
      return undefined;
    }

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return sortedPeriods[0]?.extractDate;
  }

  @computed('args.sortedPeriods.[]')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get minLeadtime() {
    const { sortedPeriods } = this.args;

    if (!sortedPeriods) {
      return undefined;
    }

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return sortedPeriods[sortedPeriods.length - 1]?.extractDate;
  }

  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get pmsIntegrationDate() {
    return this.hotelAndMarketService.hotel?.pmsIntegrationDate;
  }

  @computed('args.rentalData.occupancies.[]')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rentalOccupanciesMap() {
    const rentalOccupancies = {};
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    this.args.rentalData?.occupancies.forEach((occupancy) => {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      rentalOccupancies[occupancy.formattedExtractDate] = occupancy;
    });
    return rentalOccupancies;
  }

  <template>
    <div class="relative">
      <GeneralChartsOtaChart
        {{! @glint-expect-error: Incorrect type }}
        @chartOptions={{this.chartOptions}}
        @highStockEnabled={{true}}
        @model={{this.chartData}}
      />
      {{! @glint-expect-error: Incorrect type }}
      {{#if @message.hasMessage}}
        <div class="message-overlay">
          <div class="message-wrapper">
            {{! @glint-expect-error: Incorrect type }}
            {{@message.message}}
          </div>
        </div>
      {{/if}}
    </div>
    <div class="hidden" id="dummy-div-for-tooltip-booking-curve">
      <div class="tooltip-otb-container display-over-modal">
        <GeneralTooltipsTooltipOtbPace
          @currentRowLabel={{t "general.this_year"}}
          {{! @glint-expect-error: Incorrect type }}
          @dayMoment={{@dayMoment}}
          {{! @glint-expect-error: Incorrect type }}
          @extractDate={{this.extractDate}}
          {{! @glint-expect-error: Incorrect type }}
          @occupancy={{this.firstItemPoint.occupancy}}
          @pmsIntegrationDateInCurrentRange={{this.pmsIntegrationDateInCurrentRange}}
          {{! @glint-expect-error: Incorrect type }}
          @referenceOccupancy={{this.firstItemPoint.occupancyLastYear}}
          {{! @glint-expect-error: Incorrect type }}
          @rentalOccupancy={{this.rentalOccupancy}}
          {{! @glint-expect-error: Incorrect type }}
          @rentalReferenceOccupancy={{this.rentalReferenceOccupancy}}
          @showAvailableRooms={{true}}
          @showReferenceRow={{true}}
          @showShortTermRental={{and
            this.access.hasShortTermRentalAccess
            (not this.access.hasShortTermRentalOnlyStrTab)
          }}
        />
      </div>
    </div>
  </template>
}
